import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import {
  addTaskJustification,
  getJustificationReasons,
  JustificationReason,
} from "../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { useAuth } from "../../../../firebase/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface LateTaskJustificationModalProps {
  show: boolean;
  onHide: () => void;
  taskId: number;
  onSuccess: () => void;
  justificationType: "LATE" | "AT-RISK";
}

const LateTaskJustificationModal: React.FC<LateTaskJustificationModalProps> = ({
  show,
  onHide,
  taskId,
  onSuccess,
  justificationType,
}) => {
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [reasons, setReasons] = useState<JustificationReason[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { currentUser } = useAuth();

  // Fetch reasons when modal opens
  useEffect(() => {
    if (!currentUser || !show) return;

    const fetchReasons = async () => {
      setLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const res = await getJustificationReasons(token);

        if (Array.isArray(res)) {
          setReasons(res.filter((r) => r.isActive)); // Only active reasons
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReasons();
  }, [show, currentUser]);

  const handleSubmit = async () => {
    if (!reasonId) return;

    try {
      if (!currentUser || !show) return;

      const token = await currentUser?.getIdToken(); // get token from auth

      const payload = {
        taskId,
        userId: currentUser.uid,
        justificationType: justificationType,
        reasonLookupId: reasonId,
        detailsText: comment,
      };

      const res = await addTaskJustification(token, payload);

      if ("error" in res) {
        console.error("Failed to add justification:", res.error);
        alert("Failed to save justification. Please try again.");
      } else {
        console.log("Justification saved:", res);
        onSuccess();
        onHide();
        setReasonId(null);
        setReasons([]);
        setComment("");
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Something went wrong while saving justification.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      {/* <Modal.Header closeButton>
        <Modal.Title>Late Task Justification</Modal.Title>
      </Modal.Header> */}
      <div className="modal-activity-reject-header">
        <h2 className="modal-activity-reject-title">Late Task Justification</h2>
        <button
          className="modal-activity-reject-close"
          onClick={onHide}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Reason</Form.Label>
            <Form.Select
              value={reasonId ?? ""}
              onChange={(e) => setReasonId(Number(e.target.value))}
              disabled={loading}
            >
              <option value="">Select Reason</option>
              {reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.reasonText}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn-secondary cancel-btn" onClick={onHide}>
          Cancel
        </Button>
        <Button
          className="btn btn-primary submit-btn"
          disabled={!reasonId}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LateTaskJustificationModal;
